import type {ReplaceLeafNodeTypes, IndexingDim} from "#template/geometry-processing/geometry-graph"
import {AttributeRef, ValueRef} from "#template/geometry-processing/geometry-graph"
import type {DataNodes} from "@cm/render-nodes"
import {
    createGeometryAttributeData,
    createGeometryAttributeRefData,
    createGeometryAttributeSetData,
    createGeometryValueRefData,
    isGeometryAttributeData,
    isGeometryAttributeRefData,
    isGeometryAttributeRefDataSet,
    isGeometryAttributeSetData,
    isGeometryValueRefData,
} from "#template/nodes/geometry-graph/interfaces"
import type {
    GeometryAttributeData,
    GeometryAttributeRefData,
    GeometryAttributeRefDataSet,
    GeometryAttributeSetData,
    GeometryValueData,
    GeometryValueRefData,
} from "#template/nodes/geometry-graph/interfaces"
import type {z} from "zod/v4-mini"

export const GEOMETRY_VALUE_DATA = "geometryValueData"
export const GEOMETRY_ATTRIBUTE_DATA = "geometryAttributeData"
export const GEOMETRY_ATTRIBUTE_SET_DATA = "geometryAttributeSetData"

////////////////////////////////////////////////////////////////////////////////

export type AttributeRefSet = {[key: string]: AttributeRef | AttributeRefSet}
export type GraphType = "render" | "display"

function isAttributeRefSet(data: ValueRef | AttributeRef | AttributeRefSet): data is AttributeRefSet {
    return !(data instanceof ValueRef || data instanceof AttributeRef)
}

export type GeometryAttributeSetDataToGeometryAttributeRefDataSet<T extends IndexingDim, U extends GeometryAttributeSetData<T>> = {
    [K in keyof U["data"]]: U["data"][K] extends GeometryAttributeData<T>
        ? GeometryAttributeRefData
        : U["data"][K] extends GeometryAttributeSetData<T>
          ? GeometryAttributeSetDataToGeometryAttributeRefDataSet<T, U["data"][K]>
          : never
}

export function geometryAttributeSetDataToGeometryAttributeRefDataSet<T extends IndexingDim, U extends GeometryAttributeSetData<T>>(
    attributeSetData: U,
): GeometryAttributeSetDataToGeometryAttributeRefDataSet<T, U> {
    const traverse = <D extends GeometryAttributeData<T> | GeometryAttributeSetData<T>>(data: D) => {
        return (() => {
            if (isGeometryAttributeData(data)) return data.ref!
            else if (isGeometryAttributeSetData(data)) {
                const attrRefDataSet: GeometryAttributeRefDataSet = {}
                Object.keys(data.data).forEach((key) => {
                    attrRefDataSet[key] = traverse(data.data[key])
                })
                return attrRefDataSet
            } else throw new Error(`Invalid geometry data encountered!`)
        })()
    }

    return traverse(attributeSetData) as GeometryAttributeSetDataToGeometryAttributeRefDataSet<T, U>
}

type GeometryAttributeRefDataSetToGeometryAttributeSetData<T extends IndexingDim, U extends GeometryAttributeRefDataSet> = {
    type: "attributeSet"
    dim: T
    data: {
        [K in keyof U]: U[K] extends GeometryAttributeRefData
            ? GeometryAttributeData<T>
            : U[K] extends GeometryAttributeRefDataSet
              ? GeometryAttributeRefDataSetToGeometryAttributeSetData<T, U[K]>
              : never
    }
}

export function geometryAttributeRefDataSetToGeometryAttributeSetData<T extends IndexingDim, U extends GeometryAttributeRefDataSet>(
    attributeRefDataSet: U,
    dim: T,
): GeometryAttributeRefDataSetToGeometryAttributeSetData<T, U> {
    const traverse = <U extends GeometryAttributeRefData | GeometryAttributeRefDataSet>(node: U) => {
        if (isGeometryAttributeRefData(node)) return createGeometryAttributeData(node, dim)
        else if (isGeometryAttributeRefDataSet(node)) {
            const attributeSetData: {[key: string]: GeometryAttributeData<T> | GeometryAttributeSetData<T>} = {}
            Object.keys(node).forEach((key) => {
                attributeSetData[key] = traverse(node[key])
            })
            return createGeometryAttributeSetData(attributeSetData, dim)
        } else throw new Error(`Invalid geometry data encountered!`)
    }

    return traverse(attributeRefDataSet) as GeometryAttributeRefDataSetToGeometryAttributeSetData<T, U>
}

export function attributeRefSetFromGeometryAttributeRefDataSet<T extends GeometryAttributeRefDataSet>(
    attributeRefDataSet: T,
    graphType: GraphType,
): ReplaceLeafNodeTypes<T, GeometryAttributeRefData, AttributeRef> {
    const traverse = <U extends GeometryAttributeRefData | GeometryAttributeRefDataSet>(node: U) => {
        return (() => {
            if (isGeometryAttributeRefData(node)) return graphType === "render" ? node.renderGraphRef : node.displayGraphRef
            else if (isGeometryAttributeRefDataSet(node)) {
                const attrRefSet: AttributeRefSet = {}
                Object.keys(node).forEach((key) => {
                    attrRefSet[key] = traverse(node[key])
                })
                return attrRefSet
            } else throw new Error(`Invalid geometry data encountered!`)
        })()
    }

    return traverse(attributeRefDataSet) as ReplaceLeafNodeTypes<T, GeometryAttributeRefData, AttributeRef>
}

export function geometryAttributeRefDataSetFromAttributeRefSets<T extends AttributeRefSet>(
    renderGraphAttributeRefSet: T,
    displayGraphAttributeRefSet: T,
    displayGraphResources: DataNodes.EvaluationResources,
): ReplaceLeafNodeTypes<T, AttributeRef, GeometryAttributeRefData> {
    type TraverseRet<K extends AttributeRef | AttributeRefSet> = K extends AttributeRef ? GeometryAttributeRefData : GeometryAttributeRefDataSet

    const haveSetsSameKeys = (renderGraphNode: AttributeRefSet, displayGraphNode: AttributeRefSet): boolean => {
        const renderKeys = Object.keys(renderGraphNode)
        const displayKeys = Object.keys(displayGraphNode)
        return displayKeys.length === renderKeys.length && displayKeys.every((key) => renderKeys.includes(key))
    }

    const traverse = <U extends AttributeRef | AttributeRefSet>(renderGraphNode: U, displayGraphNode: U): TraverseRet<U> => {
        return (() => {
            if (renderGraphNode instanceof AttributeRef && displayGraphNode instanceof AttributeRef)
                return createGeometryAttributeRefData(renderGraphNode, displayGraphNode, displayGraphResources)
            else if (
                !(renderGraphNode instanceof AttributeRef) &&
                !(displayGraphNode instanceof AttributeRef) &&
                typeof renderGraphNode === "object" &&
                typeof displayGraphNode === "object"
            ) {
                if (!haveSetsSameKeys(renderGraphNode, displayGraphNode)) {
                    throw new Error("Render and display graph attribute ref sets do not have the same keys")
                }

                const attributeRefDataSet: GeometryAttributeRefDataSet = {}
                Object.keys(displayGraphNode).forEach((key) => {
                    attributeRefDataSet[key] = traverse(renderGraphNode[key], displayGraphNode[key])
                })
                return attributeRefDataSet
            } else throw new Error(`Invalid geometry attribute ref data encountered!`)
        })() as TraverseRet<U>
    }

    return traverse(renderGraphAttributeRefSet, displayGraphAttributeRefSet) as ReplaceLeafNodeTypes<T, AttributeRef, GeometryAttributeRefData>
}

export function collectDisplayGraphResourcesFromGeometryAttributeRefDataSet(attributeRefDataSet: GeometryAttributeRefDataSet): DataNodes.EvaluationResources {
    let evaluationResources: DataNodes.EvaluationResources = {}

    const traverse = <U extends GeometryAttributeRefData | GeometryAttributeRefDataSet>(node: U) => {
        if (isGeometryAttributeRefData(node)) {
            evaluationResources = {
                ...evaluationResources,
                ...node.displayGraphResources,
            }
        } else if (isGeometryAttributeRefDataSet(node)) Object.keys(node).forEach((value) => traverse(node[value]))
        else throw new Error(`Invalid geometry attribute ref data encountered!`)
    }

    traverse(attributeRefDataSet)
    return evaluationResources
}

type InvalidParam = "INVALID_PARAM"
type OperatorParam = AttributeRef | AttributeRefSet | ValueRef | number | (ValueRef | number)[] | undefined
type MapOperatorParam<T extends OperatorParam> = T extends AttributeRef
    ? GeometryAttributeRefData
    : T extends AttributeRefSet
      ? GeometryAttributeRefDataSet
      : T extends ValueRef
        ? GeometryValueRefData
        : T extends number
          ? number
          : T extends (ValueRef | number)[]
            ? (GeometryValueRefData | number)[]
            : T extends undefined
              ? undefined
              : InvalidParam

type MapOperatorArgs_<T extends OperatorParam[]> = {[K in keyof T]: MapOperatorParam<T[K]>}
type MapOperatorArgs<T extends OperatorParam[]> = T[number] extends InvalidParam ? never : MapOperatorArgs_<T>
type OperatorRet = Exclude<OperatorParam, number | (ValueRef | number)[] | undefined>
type MapOperatorRet<T extends OperatorRet> = T extends AttributeRef | ValueRef
    ? MapOperatorParam<T>
    : T extends AttributeRefSet
      ? {[K in keyof T]: MapOperatorRet<T[K]>}
      : never

export function applyGeometryOperator<T extends OperatorParam[], R extends OperatorRet>(
    operator: (...args: T) => R,
    ...args: MapOperatorArgs<T>
): MapOperatorRet<R> {
    const isArrayOfGeometryValueRefDataOrNumber = (arg: MapOperatorParam<OperatorParam>): arg is (GeometryValueRefData | number)[] => {
        return arg instanceof Array && arg.every((item) => typeof item === "number" || isGeometryValueRefData(item))
    }

    const argForGraphType = <U extends OperatorParam>(arg: MapOperatorParam<U>, graphType: GraphType): U => {
        return (() => {
            if (arg === undefined) return undefined
            else if (typeof arg === "number") return arg
            else if (isArrayOfGeometryValueRefDataOrNumber(arg))
                return arg.map((item) => (typeof item === "number" ? item : graphType === "render" ? item.renderGraphRef : item.displayGraphRef))
            else if (isGeometryValueRefData(arg)) return graphType === "render" ? arg.renderGraphRef : arg.displayGraphRef
            else if (isGeometryAttributeRefData(arg)) return graphType === "render" ? arg.renderGraphRef : arg.displayGraphRef
            else if (isGeometryAttributeRefDataSet(arg)) return attributeRefSetFromGeometryAttributeRefDataSet(arg, graphType)
            else throw new Error(`Invalid argument type for graph type ${graphType}`)
        })() as U
    }

    const argsForGraphType = (graphType: GraphType): T => {
        return args.map((arg) => argForGraphType(arg, graphType)) as T
    }

    const collectDisplayGraphResourcesFromArrayOfGeometryValueRefDataOrNumber = (arg: (GeometryValueRefData | number)[]): DataNodes.EvaluationResources => {
        let evaluationResources: DataNodes.EvaluationResources = {}
        arg.filter((item) => typeof item !== "number").forEach((item) => (evaluationResources = {...evaluationResources, ...item.displayGraphResources}))
        return evaluationResources
    }

    const collectDisplayGraphResourcesFromArg = <U extends OperatorParam>(arg: MapOperatorParam<U>): DataNodes.EvaluationResources => {
        if (arg === undefined) return {}
        else if (typeof arg === "number") return {}
        else if (isArrayOfGeometryValueRefDataOrNumber(arg)) return collectDisplayGraphResourcesFromArrayOfGeometryValueRefDataOrNumber(arg)
        else if (isGeometryValueRefData(arg)) return {...arg.displayGraphResources}
        else if (isGeometryAttributeRefData(arg)) return {...arg.displayGraphResources}
        else if (isGeometryAttributeRefDataSet(arg)) {
            return collectDisplayGraphResourcesFromGeometryAttributeRefDataSet(arg)
        } else throw new Error(`Invalid argument type encountered when collecting display graph resources!`)
    }

    const collectDisplayGraphResourcesFromArgs = (): DataNodes.EvaluationResources => {
        let evaluationResources: DataNodes.EvaluationResources = {}
        args.forEach((arg) => {
            evaluationResources = {
                ...collectDisplayGraphResourcesFromArg(arg),
                ...evaluationResources,
            }
        })
        return evaluationResources
    }

    const renderGraphOperatorArgs = argsForGraphType("render")
    const displayGraphOperatorArgs = argsForGraphType("display")
    const displayGraphResources = collectDisplayGraphResourcesFromArgs()

    const renderGraphOperatorOutput = operator(...renderGraphOperatorArgs)
    const displayGraphOperatorOutput = operator(...displayGraphOperatorArgs)

    return (() => {
        if (renderGraphOperatorOutput instanceof ValueRef && displayGraphOperatorOutput instanceof ValueRef)
            return createGeometryValueRefData(renderGraphOperatorOutput, displayGraphOperatorOutput, displayGraphResources)
        else if (renderGraphOperatorOutput instanceof AttributeRef && displayGraphOperatorOutput instanceof AttributeRef)
            return createGeometryAttributeRefData(renderGraphOperatorOutput, displayGraphOperatorOutput, displayGraphResources)
        else if (isAttributeRefSet(renderGraphOperatorOutput) && isAttributeRefSet(displayGraphOperatorOutput)) {
            return geometryAttributeRefDataSetFromAttributeRefSets(renderGraphOperatorOutput, displayGraphOperatorOutput, displayGraphResources)
        } else throw new Error(`Invalid operator output type encountered!`)
    })() as MapOperatorRet<R>
}

export function resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber(
    data: GeometryAttributeData | GeometryValueData | number,
): GeometryValueRefData | number {
    return typeof data === "number" ? data : data.ref
}

export type ZodNode<T> = z.ZodMiniType<T, T>
export type NonNullableParameters<T> = {[K in keyof T]: NonNullable<T[K]>}
